<template>
  <div class="welcome-step">
    <div class="welcome-content">
      <!-- AI Avatar -->
      <div class="avatar-container">
        <img :src="avatarImage" alt="AI Assistant" class="ai-avatar" />
      </div>

      <!-- Title -->
      <div class="title-section">
        <h1 class="step-title h1">{{ step.title }}</h1>
        <p v-if="step.subtitle" class="step-subtitle body">{{ step.subtitle }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { LookStep } from '@/features/look/types/look-config'

interface Props {
  step: LookStep & { type: 'welcome' }
}

const props = defineProps<Props>()

const avatarImage = '/img/survey-avatar.png'
</script>

<style scoped>
.welcome-step {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.welcome-content {
  display: flex;
  padding: var(--Padding-XXL, 40px) 120px;
  align-items: center;
  gap: var(--Spacing-XL, 40px);
  align-self: stretch;
}

.avatar-container {
  display: flex;
  justify-content: center;
}

.ai-avatar {
  width: 234px;
  height: 234px;
  aspect-ratio: 1/1;
}

.title-section {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-L, 22px);
  text-align: left;
}

.step-title {
  font-weight: 600;
  color: var(--primary-blue-100);
  margin: 0;
}

.step-subtitle {
  color: var(--primary-blue-80);
  margin: 0;
}

@media (min-width: 1920px) {
  .welcome-content {
    padding: 40px 120px;
    flex-direction: row;
    align-items: center;
    gap: 40px;
  }

  .avatar-container {
    display: flex;
    justify-content: center;
    flex-shrink: 0;
  }

  .ai-avatar {
    width: 234px;
    height: 234px;
    border-radius: 20px;
    object-fit: cover;
  }

  .title-section {
    gap: 8px;
    text-align: left;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
  }
}

@media (min-width: 1440px) and (max-width: 1919px) {
  .welcome-content {
    padding: 40px;
    flex-direction: row;
    align-items: center;
    gap: 40px;
  }

  .avatar-container {
    display: flex;
    justify-content: center;
    flex-shrink: 0;
  }

  .ai-avatar {
    width: 234px;
    height: 234px;
    border-radius: 20px;
    object-fit: cover;
  }

  .title-section {
    gap: 8px;
    text-align: left;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
  }
}

@media (min-width: 1024px) and (max-width: 1439px) {
  .welcome-content {
    padding: 40px;
    flex-direction: column;
    align-items: center;
    gap: 40px;
  }

  .avatar-container {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .ai-avatar {
    width: 234px;
    height: 234px;
    border-radius: 20px;
    object-fit: cover;
  }

  .title-section {
    align-self: stretch;
    gap: 8px;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
  }
}

@media (max-width: 1919px) {
  .welcome-content {
    display: flex;
    padding: var(--Padding-XXL, 40px);
    align-items: center;
    gap: var(--Spacing-XL, 40px);
    align-self: stretch;
  }

  .avatar-container {
    align-self: stretch;
  }
}

@media (max-width: 1365px) {
  .welcome-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    padding: var(--Padding-XXL, 40px);
  }

  .avatar-container {
    align-self: stretch;
  }
}

@media (max-width: 767px) {
  .welcome-content {
    padding: 20px 32px;
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }

  .avatar-container {
    width: 100%;
  }

  .title-section {
    align-self: stretch;
    gap: 20px;
  }

  .step-title {
    font-size: 32px;
    line-height: 1.09;
    font-weight: 700;
  }

  .step-subtitle {
    font-size: 15px;
    line-height: 1.3;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .welcome-content {
    padding: 20px 32px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 40px;
    align-self: stretch;
  }

  .avatar-container {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .title-section {
    align-self: stretch;
    gap: 20px;
  }

  .step-title {
    font-size: 32px;
    line-height: 1.09;
    font-weight: 700;
  }

  .step-subtitle {
    font-size: 15px;
    line-height: 1.3;
  }
}
</style>

